import { useEffect, useId, useRef, useState } from 'react';
import { api, type PublicShareLink } from '../api';
import type { HostedQuotaUsage, VideoItem } from '../types';
import { AccordionItem } from './AccordionItem';
import { SectionTabs } from './SectionTabs';

const PAGE_SIZE = 5;
const VIEWS = [{ id: 'current', label: 'Current links' }, { id: 'history', label: 'History' }] as const;
function isHistory(link: PublicShareLink): boolean {
  return link.state === 'revoked' || link.state === 'expired';
}
function shareTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

function ShareRow({ link, busy, readOnly, open, onToggle, onRevoke, onCopied }: {
  link: PublicShareLink; busy: boolean; readOnly: boolean;
  open: boolean; onToggle: () => void;
  onRevoke: (id: string) => Promise<void>; onCopied: (text: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [copyError, setCopyError] = useState('');
  const revokeButton = useRef<HTMLButtonElement>(null);
  const copyButton = useRef<HTMLButtonElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);
  const returnFocus = useRef(false);
  useEffect(() => {
    if (confirming) cancelButton.current?.focus();
    else if (returnFocus.current) {
      (revokeButton.current ?? copyButton.current)?.focus();
      returnFocus.current = false;
    }
  }, [confirming]);
  function cancel() { returnFocus.current = true; setConfirming(false); }
  const historical = isHistory(link);
  const status = { active: 'Active', expired: 'Expired', revoked: 'Revoked', unavailable: 'Unavailable' }[link.state];
  const type = link.kind === 'permanent' ? 'Permanent' : 'Temporary · 24 hours';
  return <AccordionItem title={link.title} description={type} label={`Share link: ${link.title}`} open={open} onToggle={onToggle}
    badge={<span className={`public-share-status public-share-status--${link.state}`}>{status}</span>}>
    <article className={`public-share-row${historical ? ' public-share-row--history' : ''}`} data-share-id={link.id}>
    {!historical && <p>{link.kind === 'permanent' ? 'Permanent · no automatic expiry' : 'Temporary · 24 hours from creation'}</p>}
    {link.allowOriginalDownload !== undefined && <p>{link.allowOriginalDownload ? 'Original download allowed' : 'Preview only · original download disabled'}</p>}
    <dl className="public-share-row__dates"><div><dt>Created</dt><dd><time dateTime={link.createdAt}>{shareTime(link.createdAt)}</time></dd></div>
      {link.state === 'revoked' ? <div><dt>Revoked</dt><dd>{link.revokedAt ? <time dateTime={link.revokedAt}>{shareTime(link.revokedAt)}</time> : 'Time unavailable'}</dd></div>
        : link.expiresAt && <div><dt>{historical ? 'Expired' : 'Expires'}</dt><dd><time dateTime={link.expiresAt}>{shareTime(link.expiresAt)}</time></dd></div>}</dl>
    <code className="public-share-url">{link.url}</code>
    {copyError && <p className="form-alert" role="alert">{copyError}</p>}
    {!historical && <div className="public-share-row__actions">
      <button ref={copyButton} className="button button--secondary" type="button" disabled={busy} onClick={() => {
        void Promise.resolve().then(() => navigator.clipboard.writeText(link.url)).then(() => { setCopyError(''); onCopied('Share link copied.'); })
          .catch(() => setCopyError('Copy failed. Select and copy the link above.'));
      }}>Copy link</button>
      <a className="button button--secondary" href={link.url} target="_blank" rel="noopener noreferrer">Open link</a>
      {!readOnly && !confirming && <button ref={revokeButton} className="button button--secondary" type="button" disabled={busy} onClick={() => setConfirming(true)}>Revoke link</button>}
    </div>}
    {confirming && !readOnly && <div className="public-share-confirm" role="group" data-modal-escape-boundary aria-label="Confirm link revocation" onKeyDown={(event) => { if (event.key === 'Escape' && !busy) { event.preventDefault(); event.stopPropagation(); cancel(); } }}>
      <p>Stop sharing through this link? Other links and embeds stay available.</p>
      <div className="public-share-row__actions"><button className="button button--danger" type="button" disabled={busy} onClick={() => { void onRevoke(link.id).catch(() => undefined); }}>{busy ? 'Revoking…' : 'Revoke this link'}</button>
        <button ref={cancelButton} className="button button--secondary" type="button" disabled={busy} onClick={cancel}>Cancel</button></div>
    </div>}
    </article>
  </AccordionItem>;
}
export function PublicSharesPanel({ video, readOnly = false, onCopied }: { video: VideoItem; readOnly?: boolean; onCopied: (text: string) => void }) {
  const isImage = video.mediaKind === 'image';
  const [allowOriginalDownload, setAllowOriginalDownload] = useState(false);
  const snapshot = () => isImage ? api.imagePublicShareSnapshot(video.id) : api.publicShareSnapshot(video.id);
  const [links, setLinks] = useState<PublicShareLink[]>([]);
  const [usage, setUsage] = useState<HostedQuotaUsage | null>(null);
  const [kind, setKind] = useState<'permanent' | 'temporary_24h'>('temporary_24h');
  const [title, setTitle] = useState(video.title.slice(0, 200));
  const [description, setDescription] = useState((video.description ?? '').slice(0, 4000));
  const [creatorName, setCreatorName] = useState('');
  const [loading, setLoading] = useState(true);
  const [listFailed, setListFailed] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [view, setView] = useState<typeof VIEWS[number]['id']>('current');
  const [page, setPage] = useState(0);
  const [expandedLinkId, setExpandedLinkId] = useState<string | null>(null);
  const tabsId = useId();
  const section = useRef<HTMLElement>(null);
  const previousPageButton = useRef<HTMLButtonElement>(null);
  const nextPageButton = useRef<HTMLButtonElement>(null);
  const paginationFocus = useRef<'previous' | 'next' | null>(null);
  const alive = useRef(true);
  function loadLinks() {
    setLoading(true); setListFailed(false); setRefreshFailed(false); setError('');
    void snapshot().then(({ links: rows, usage: nextUsage }) => { if (alive.current) { setLinks(rows); setUsage(nextUsage); } })
      .catch(() => { if (alive.current) { setListFailed(true); setError('Share links could not be loaded. Reload the list to try again.'); } })
      .finally(() => { if (alive.current) setLoading(false); });
  }
  useEffect(() => {
    alive.current = true;
    loadLinks();
    return () => { alive.current = false; };
  }, [video.id]);
  async function revoke(id: string) {
    const focusedRow = document.activeElement?.closest('.public-share-row');
    const restoreFocus = focusedRow?.getAttribute('data-share-id') === id;
    setBusy(true); setError('');
    try {
      await api.revokePublicShare(id);
      if (alive.current) {
        setLinks(current => current.map(link => link.id === id ? { ...link, state: 'revoked' } : link));
        setExpandedLinkId(current => current === id ? null : current);
        setNotice('Share link revoked and moved to History. Other links and embeds stay available.');
        onCopied('Share link revoked and moved to History.');
        if (restoreFocus && (document.activeElement === document.body || focusedRow?.contains(document.activeElement))) {
          section.current?.querySelector<HTMLButtonElement>('[role="tab"][aria-selected="true"]')?.focus({ preventScroll: true });
        }
        // Read the authoritative revocation timestamp; never invent audit time locally.
        try {
          const { links: rows, usage: nextUsage } = await snapshot();
          if (alive.current) {
            setLinks(rows.map(link => link.id === id ? { ...link, state: 'revoked' } : link));
            setUsage(nextUsage);
            setRefreshFailed(false);
          }
        } catch {
          if (alive.current) {
            setRefreshFailed(true);
            setNotice('Share link was revoked. Usage could not be refreshed; reload share links to confirm the available slot.');
          }
        }
      }
    } catch (reason) {
      if (alive.current) setError(reason instanceof Error ? reason.message : 'This link could not be revoked.');
      throw reason;
    } finally { if (alive.current) setBusy(false); }
  }
  const selectedLinks = links.filter(link => isHistory(link) === (view === 'history'));
  const pageCount = Math.max(1, Math.ceil(selectedLinks.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const visibleLinks = selectedLinks.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  useEffect(() => {
    if (paginationFocus.current === 'next') {
      (currentPage === pageCount - 1 ? previousPageButton.current : nextPageButton.current)?.focus();
    } else if (paginationFocus.current === 'previous') {
      (currentPage === 0 ? nextPageButton.current : previousPageButton.current)?.focus();
    }
    paginationFocus.current = null;
  }, [currentPage, pageCount]);
  return <section ref={section} className="workspace-section public-shares-panel" aria-label="Public share links">
    <div className="workspace-section__heading"><div><h3>{isImage ? 'Share privately' : 'Public share links'}</h3><p>{isImage ? 'Create a share page for people with the link. Links can be forwarded. This does not publish the image CDN.' : 'Create a page for people with the link. Your existing embeds keep their own permissions.'}</p>{usage && <small>{usage.active} / {usage.limit} active links in this plan. Revoke a link to free a slot.</small>}</div></div>
    {error && <p className="form-alert" role="alert">{error}</p>}
    {notice && <p className="form-help" role="status">{notice}</p>}
    {(listFailed || refreshFailed) && <button className="button button--secondary" type="button" onClick={loadLinks} disabled={loading || busy}>Reload share links</button>}
    <div className={`public-share-workspace${readOnly ? ' public-share-workspace--read-only' : ''}`}>
    {!readOnly && <form className="dialog-form public-share-form public-share-form-card" onSubmit={(event) => {
      event.preventDefault(); setBusy(true); setError('');
      const creation = isImage ? api.createImagePublicShare(video.id, { kind, title, description, creatorName, allowOriginalDownload }) : api.createPublicShare(video.id, { kind, title, description, creatorName });
      void creation.then(async (link) => {
        if (alive.current) {
          setLinks(current => [link, ...current]); setView('current'); setPage(0); setExpandedLinkId(null);
          setNotice('Share link created. It is shown first in Current links.'); onCopied('Share link created.');
          try {
            const { links: rows, usage: nextUsage } = await snapshot();
            if (alive.current) {
              setLinks(rows.some((row) => row.id === link.id) ? rows : [link, ...rows]);
              setUsage(nextUsage); setRefreshFailed(false);
            }
          } catch {
            if (alive.current) {
              setRefreshFailed(true);
              setNotice('Share link created. Usage could not be refreshed; reload share links to confirm the active count.');
            }
          }
        }
      }).catch(reason => { if (alive.current) setError(reason instanceof Error ? reason.message : 'This link could not be created.'); })
        .finally(() => { if (alive.current) setBusy(false); });
    }}>
      <fieldset className="form-section" disabled={busy || loading || listFailed}><legend>Create a share link</legend>
        <div className="choice-list">
          <label className="checkbox-field"><input className="control-choice" type="radio" name="share-kind" checked={kind === 'temporary_24h'} onChange={() => setKind('temporary_24h')} /><span>Temporary · 24 hours</span></label>
          <label className="checkbox-field"><input className="control-choice" type="radio" name="share-kind" checked={kind === 'permanent'} onChange={() => setKind('permanent')} /><span>Permanent · until revoked</span></label>
        </div>
        <div className="form-section__grid">
          <label>Public title<input value={title} maxLength={200} required onChange={event => setTitle(event.target.value)} /></label>
          <label>Public creator name (optional)<input value={creatorName} maxLength={100} autoComplete="off" onChange={event => setCreatorName(event.target.value)} /></label>
        </div>
        <label>Public description (optional)<textarea value={description} maxLength={4000} rows={4} onChange={event => setDescription(event.target.value)} /></label>
        {isImage && <label className="checkbox-field"><input className="control-choice" type="checkbox" checked={allowOriginalDownload} onChange={event => setAllowOriginalDownload(event.target.checked)} /><span>Allow original download<small>Off means preview only. Visible previews can still be saved or captured.</small></span></label>}
        <button type="submit" className="button button--primary" disabled={busy || !title.trim()}>{busy ? 'Saving…' : 'Create share link'}</button>
      </fieldset>
    </form>}
    <section className="workspace-section public-share-browser" aria-label="Share link list">
      {readOnly && <p className="public-share-empty">Share changes are managed by the {isImage ? 'image' : 'video'} owner.</p>}
      <SectionTabs id={tabsId} label="Share link views" tabs={VIEWS} value={view} onChange={next => { setView(next); setPage(0); setExpandedLinkId(null); }} />
      <div id={`${tabsId}-panel-${view}`} role="tabpanel" aria-labelledby={`${tabsId}-tab-${view}`} tabIndex={0} className="public-share-results" aria-busy={loading}>
        <p className="form-help">{view === 'current' ? 'Active and unavailable links. Revoked and expired links are in History.' : 'Revoked and expired links are kept for reference. To share again, create a new link.'}</p>
        {loading ? <p role="status">Loading share links…</p> : !listFailed && <>
          {selectedLinks.length > 0 && <nav className="public-share-pagination" aria-label="Share link pages">
            <span role="status">{currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, selectedLinks.length)} of {selectedLinks.length} links · Page {currentPage + 1} of {pageCount}</span>
            {pageCount > 1 && <div className="public-share-row__actions">
              <button ref={previousPageButton} className="button button--secondary" type="button" disabled={busy || currentPage === 0} onClick={() => { paginationFocus.current = 'previous'; setExpandedLinkId(null); setPage(currentPage - 1); }}>Previous page</button>
              <button ref={nextPageButton} className="button button--secondary" type="button" disabled={busy || currentPage === pageCount - 1} onClick={() => { paginationFocus.current = 'next'; setExpandedLinkId(null); setPage(currentPage + 1); }}>Next page</button>
            </div>}
          </nav>}
          {visibleLinks.length ? <div className="accordion-list public-share-list">{visibleLinks.map(link =>
            <ShareRow key={`${view}-${link.id}`} link={link} busy={busy} readOnly={readOnly} open={expandedLinkId === link.id}
              onToggle={() => setExpandedLinkId(current => current === link.id ? null : link.id)} onRevoke={revoke} onCopied={onCopied} />)}</div>
            : <p className="public-share-empty">{view === 'history' ? 'No revoked or expired links.' : links.length ? 'No current share links. Previously shared links are in History.' : `No public share links. This ${isImage ? 'image' : 'video'} has not been shared through a public page.`}</p>}
        </>}
      </div>
    </section>
    </div>
  </section>;
}
